import { usePage } from "@inertiajs/react";
import { useMemo } from "react";

import {
  activityLogOrganizationPurchaseRequestPath,
  addAliasOrganizationProductPath,
  bulkAssignRequestItemOrganizationQuoteItemsPath,
  bulkUpsertOrganizationPurchaseRequestPurchaseSelectionsPath,
  completeExportDownloadOrganizationPurchaseRequestPath,
  completeExportStatusOrganizationPurchaseRequestPath,
  createDirectPurchaseOrganizationInvoicePath,
  createFromPdfOrganizationPurchaseRequestsPath,
  editOrganizationCategoryPath,
  editOrganizationCategorySubcategoryPath,
  editOrganizationCompanyPath,
  editOrganizationProductPath,
  editOrganizationPurchaseRequestPath,
  editOrganizationSupplierPath,
  exportOrganizationApprovalsWorkflowsPath,
  exportPriceComparisonOrganizationPurchaseRequestPath,
  exportPurchaseOrdersOrganizationPurchaseRequestPath,
  finalizeQuickOrganizationPurchaseRequestPath,
  importNewOrganizationApprovalsWorkflowsPath,
  importNewOrganizationProductsPath,
  importNewOrganizationSectorsPath,
  importNewOrganizationSuppliersPath,
  importOrganizationApprovalsWorkflowsPath,
  importOrganizationMembersPath,
  importOrganizationProductsPath,
  importOrganizationSectorsPath,
  importOrganizationSuppliersPath,
  markAllAsReadNotificationsPath,
  markAsReadNotificationPath,
  markAsReviewedOrganizationDeliveryNotePath,
  markAsReviewedOrganizationInvoicePath,
  matchItemsOrganizationDeliveryNotePath,
  newOrganizationCategoryPath,
  newOrganizationCategorySubcategoryPath,
  newOrganizationCompanyPath,
  newOrganizationMemberPath,
  newOrganizationProductPath,
  newOrganizationPurchaseRequestPath,
  newOrganizationPurchaseRequestQuotePath,
  newOrganizationSectorPath,
  newOrganizationSupplierPath,
  newQuoteRequestOrganizationPurchaseRequestPath,
  notificationsPath,
  organizationApprovalsPath,
  organizationCategoriesPath,
  organizationCategoryPath,
  organizationCategorySubcategoriesPath,
  organizationCategorySubcategoryPath,
  organizationCompaniesPath,
  organizationCompanyPath,
  organizationDailyActivityPath,
  organizationDashboardPath,
  organizationDeliveryNoteCommentsPath,
  organizationDeliveryNoteItemsPath,
  organizationDeliveryNotePath,
  organizationDeliveryNotesPath,
  organizationInvoiceCommentsPath,
  organizationInvoiceItemsPath,
  organizationInvoicePath,
  organizationInvoicesPath,
  organizationMemberPath,
  organizationMembersPath,
  organizationPricesPath,
  organizationProductPath,
  organizationProductsPath,
  organizationPurchaseRequestApprovalFlowPath,
  organizationPurchaseRequestApprovalsPath,
  organizationPurchaseRequestCommentsPath,
  organizationPurchaseRequestPath,
  organizationPurchaseRequestQuotePath,
  organizationPurchaseRequestQuotesPath,
  organizationPurchaseRequestsPath,
  organizationQuoteDocumentPath,
  organizationQuoteItemPath,
  organizationQuoteItemsPath,
  organizationQuotePath,
  organizationQuotesPath,
  organizationRequestItemPath,
  organizationRequestItemsPath,
  organizationSectorApprovalWorkflowPath,
  organizationSectorPath,
  organizationSectorsPath,
  organizationSettingsOrganizationPath,
  organizationSupplierPath,
  organizationSuppliersPath,
  quickCreateOrganizationPurchaseRequestsPath,
  quickNewOrganizationPurchaseRequestsPath,
  quickStartOrganizationPurchaseRequestsPath,
  removeAliasOrganizationProductPath,
  requestCompleteExportOrganizationPurchaseRequestPath,
  resendOrganizationPurchaseRequestQuoteInvitationPath,
  searchOrganizationCategoriesPath,
  searchOrganizationCategorySubcategoriesPath,
  searchOrganizationMembersPath,
  searchOrganizationProductsPath,
  searchOrganizationPurchaseRequestsPath,
  searchOrganizationSectorsPath,
  searchOrganizationSuppliersPath,
  simpleNewOrganizationPurchaseRequestsPath,
  uploadAndProcessQuotesOrganizationPurchaseRequestPath,
  uploadNewOrganizationDeliveryNotesPath,
  uploadNewOrganizationInvoicesPath,
  uploadNewOrganizationPurchaseRequestsPath,
} from "@/routes";
import type { SharedData } from "@/types";

type RouteId = number | string;

/** Tipo base para rutas de recursos estándar (index, show, new, edit) */
interface ResourceRoutes {
  index: () => string;
  show: (id: RouteId) => string;
  new: () => string;
  edit: (id: RouteId) => string;
}

/**
 * Hook que centraliza la construcción de rutas tenant-scoped.
 *
 * Usa los helpers generados por js-routes para mantener sincronización con Rails.
 * Las rutas están agrupadas por recurso para mejor organización y discoverability.
 *
 * @example
 * const routes = useOrganizationRoutes();
 * routes.purchaseRequests.index() // "/organizations/acme/purchase_requests"
 * routes.purchaseRequests.show(123) // "/organizations/acme/purchase_requests/123"
 * routes.purchaseRequests.quotes.new(123) // "/organizations/acme/purchase_requests/123/quotes/new"
 */
export function useOrganizationRoutes() {
  const { auth } = usePage<SharedData>().props;
  const organizationSlug = auth.current_organization?.slug ?? "";

  return useMemo(() => {
    const slug = organizationSlug;

    return {
      /** Slug de la organización actual */
      organizationSlug: slug,

      /** Dashboard de la organización */
      dashboard: () => organizationDashboardPath(slug),

      /** Actividad diaria de la organización */
      dailyActivity: () => organizationDailyActivityPath(slug),

      /** Settings de la organización */
      settings: () => organizationSettingsOrganizationPath(slug),

      // ─────────────────────────────────────────────────────────────────────
      // Notificaciones
      // ─────────────────────────────────────────────────────────────────────
      notifications: {
        index: () => notificationsPath(),
        markAsRead: (id: RouteId) => markAsReadNotificationPath(id),
        markAllAsRead: () => markAllAsReadNotificationsPath(),
      },

      // ─────────────────────────────────────────────────────────────────────
      // Purchase Requests
      // ─────────────────────────────────────────────────────────────────────
      purchaseRequests: {
        index: () => organizationPurchaseRequestsPath(slug),
        show: (id: RouteId) => organizationPurchaseRequestPath(slug, id),
        new: () => newOrganizationPurchaseRequestPath(slug),
        edit: (id: RouteId) => editOrganizationPurchaseRequestPath(slug, id),
        // Variantes de creación
        simpleNew: () => simpleNewOrganizationPurchaseRequestsPath(slug),
        quickNew: () => quickNewOrganizationPurchaseRequestsPath(slug),
        quickStart: () => quickStartOrganizationPurchaseRequestsPath(slug),
        quickCreate: () => quickCreateOrganizationPurchaseRequestsPath(slug),
        uploadNew: () => uploadNewOrganizationPurchaseRequestsPath(slug),
        createFromPdf: () =>
          createFromPdfOrganizationPurchaseRequestsPath(slug),
        finalizeQuick: (id: RouteId) =>
          finalizeQuickOrganizationPurchaseRequestPath(slug, id),
        // Acciones
        uploadAndProcessQuotes: (id: RouteId) =>
          uploadAndProcessQuotesOrganizationPurchaseRequestPath(slug, id),
        comments: (id: RouteId) =>
          organizationPurchaseRequestCommentsPath(slug, id),
        activityLog: (id: RouteId) =>
          activityLogOrganizationPurchaseRequestPath(slug, id),
        search: () => searchOrganizationPurchaseRequestsPath(slug),
        // Exports
        pdf: (id: RouteId) =>
          organizationPurchaseRequestPath(slug, id, { format: "pdf" }),
        xlsx: (id: RouteId) =>
          organizationPurchaseRequestPath(slug, id, { format: "xlsx" }),
        exportPriceComparison: (id: RouteId) =>
          exportPriceComparisonOrganizationPurchaseRequestPath(slug, id),
        exportPurchaseOrders: (
          id: RouteId,
          options?: { supplier_ids?: number[] },
        ) =>
          exportPurchaseOrdersOrganizationPurchaseRequestPath(
            slug,
            id,
            options,
          ),
        requestCompleteExport: (id: RouteId) =>
          requestCompleteExportOrganizationPurchaseRequestPath(slug, id),
        completeExportStatus: (id: RouteId) =>
          completeExportStatusOrganizationPurchaseRequestPath(slug, id),
        completeExportDownload: (id: RouteId) =>
          completeExportDownloadOrganizationPurchaseRequestPath(slug, id),
        // Quotes anidados
        quotes: {
          index: (prId: RouteId) =>
            organizationPurchaseRequestQuotesPath(slug, prId),
          show: (prId: RouteId, quoteId: RouteId) =>
            organizationPurchaseRequestQuotePath(slug, prId, quoteId),
          new: (prId: RouteId) =>
            newOrganizationPurchaseRequestQuotePath(slug, prId),
          newQuoteRequest: (prId: RouteId) =>
            newQuoteRequestOrganizationPurchaseRequestPath(slug, prId),
        },
        // Quote Invitations anidados
        quoteInvitations: {
          resend: (prId: RouteId, invitationId: RouteId) =>
            resendOrganizationPurchaseRequestQuoteInvitationPath(
              slug,
              prId,
              invitationId,
            ),
        },
        // Approvals anidados
        approvalFlow: (prId: RouteId) =>
          organizationPurchaseRequestApprovalFlowPath(slug, prId),
        approvals: (prId: RouteId) =>
          organizationPurchaseRequestApprovalsPath(slug, prId),
        // Purchase Selections anidados
        purchaseSelections: {
          bulkUpsert: (prId: RouteId) =>
            bulkUpsertOrganizationPurchaseRequestPurchaseSelectionsPath(
              slug,
              prId,
            ),
        },
      } satisfies ResourceRoutes & Record<string, unknown>,

      // ─────────────────────────────────────────────────────────────────────
      // Request Items
      // ─────────────────────────────────────────────────────────────────────
      requestItems: {
        index: () => organizationRequestItemsPath(slug),
        show: (id: RouteId) => organizationRequestItemPath(slug, id),
      },

      // ─────────────────────────────────────────────────────────────────────
      // Quotes (standalone, no anidados)
      // ─────────────────────────────────────────────────────────────────────
      quotes: {
        index: () => organizationQuotesPath(slug),
        show: (id: RouteId) => organizationQuotePath(slug, id),
      },

      // ─────────────────────────────────────────────────────────────────────
      // Quote Items
      // ─────────────────────────────────────────────────────────────────────
      quoteItems: {
        index: () => organizationQuoteItemsPath(slug),
        show: (id: RouteId) => organizationQuoteItemPath(slug, id),
        bulkAssignRequestItem: () =>
          bulkAssignRequestItemOrganizationQuoteItemsPath(slug),
      },

      // ─────────────────────────────────────────────────────────────────────
      // Quote Documents
      // ─────────────────────────────────────────────────────────────────────
      quoteDocuments: {
        show: (id: RouteId) => organizationQuoteDocumentPath(slug, id),
      },

      // ─────────────────────────────────────────────────────────────────────
      // Products
      // ─────────────────────────────────────────────────────────────────────
      products: {
        index: () => organizationProductsPath(slug),
        show: (id: RouteId) => organizationProductPath(slug, id),
        new: () => newOrganizationProductPath(slug),
        edit: (id: RouteId) => editOrganizationProductPath(slug, id),
        search: () => searchOrganizationProductsPath(slug),
        import: () => importOrganizationProductsPath(slug),
        importNew: () => importNewOrganizationProductsPath(slug),
        addAlias: (id: RouteId) => addAliasOrganizationProductPath(slug, id),
        removeAlias: (id: RouteId) =>
          removeAliasOrganizationProductPath(slug, id),
      } satisfies ResourceRoutes & Record<string, unknown>,

      // ─────────────────────────────────────────────────────────────────────
      // Categories
      // ─────────────────────────────────────────────────────────────────────
      categories: {
        index: () => organizationCategoriesPath(slug),
        show: (id: RouteId) => organizationCategoryPath(slug, id),
        new: () => newOrganizationCategoryPath(slug),
        edit: (id: RouteId) => editOrganizationCategoryPath(slug, id),
        search: () => searchOrganizationCategoriesPath(slug),
        subcategories: {
          index: (categoryId: RouteId) =>
            organizationCategorySubcategoriesPath(slug, categoryId),
          show: (categoryId: RouteId, subcategoryId: RouteId) =>
            organizationCategorySubcategoryPath(
              slug,
              categoryId,
              subcategoryId,
            ),
          new: (categoryId: RouteId) =>
            newOrganizationCategorySubcategoryPath(slug, categoryId),
          edit: (categoryId: RouteId, subcategoryId: RouteId) =>
            editOrganizationCategorySubcategoryPath(
              slug,
              categoryId,
              subcategoryId,
            ),
          search: (categoryId: RouteId) =>
            searchOrganizationCategorySubcategoriesPath(slug, categoryId),
        },
      } satisfies ResourceRoutes & Record<string, unknown>,

      // ─────────────────────────────────────────────────────────────────────
      // Companies
      // ─────────────────────────────────────────────────────────────────────
      companies: {
        index: () => organizationCompaniesPath(slug),
        show: (id: RouteId) => organizationCompanyPath(slug, id),
        new: () => newOrganizationCompanyPath(slug),
        edit: (id: RouteId) => editOrganizationCompanyPath(slug, id),
      } satisfies ResourceRoutes,

      // ─────────────────────────────────────────────────────────────────────
      // Suppliers
      // ─────────────────────────────────────────────────────────────────────
      suppliers: {
        index: () => organizationSuppliersPath(slug),
        show: (id: RouteId) => organizationSupplierPath(slug, id),
        new: () => newOrganizationSupplierPath(slug),
        edit: (id: RouteId) => editOrganizationSupplierPath(slug, id),
        search: () => searchOrganizationSuppliersPath(slug),
        import: () => importOrganizationSuppliersPath(slug),
        importNew: () => importNewOrganizationSuppliersPath(slug),
      } satisfies ResourceRoutes & Record<string, unknown>,

      // ─────────────────────────────────────────────────────────────────────
      // Sectors
      // ─────────────────────────────────────────────────────────────────────
      sectors: {
        index: () => organizationSectorsPath(slug),
        show: (id: RouteId) => organizationSectorPath(slug, id),
        new: () => newOrganizationSectorPath(slug),
        search: () => searchOrganizationSectorsPath(slug),
        import: () => importOrganizationSectorsPath(slug),
        importNew: () => importNewOrganizationSectorsPath(slug),
        approvalWorkflow: (sectorId: RouteId) =>
          organizationSectorApprovalWorkflowPath(slug, sectorId),
      },

      // ─────────────────────────────────────────────────────────────────────
      // Members
      // ─────────────────────────────────────────────────────────────────────
      members: {
        index: () => organizationMembersPath(slug),
        show: (id: RouteId) => organizationMemberPath(slug, id),
        new: () => newOrganizationMemberPath(slug),
        search: () => searchOrganizationMembersPath(slug),
        import: () => importOrganizationMembersPath(slug),
      },

      // ─────────────────────────────────────────────────────────────────────
      // Delivery Notes
      // ─────────────────────────────────────────────────────────────────────
      deliveryNotes: {
        index: () => organizationDeliveryNotesPath(slug),
        show: (id: RouteId) => organizationDeliveryNotePath(slug, id),
        uploadNew: (options?: { purchase_request_id?: RouteId }) =>
          uploadNewOrganizationDeliveryNotesPath(slug, options),
        comments: (id: RouteId) =>
          organizationDeliveryNoteCommentsPath(slug, id),
        items: () => organizationDeliveryNoteItemsPath(slug),
        markAsReviewed: (id: RouteId) =>
          markAsReviewedOrganizationDeliveryNotePath(slug, id),
        matchItems: (id: RouteId) =>
          matchItemsOrganizationDeliveryNotePath(slug, id),
      },

      // ─────────────────────────────────────────────────────────────────────
      // Invoices
      // ─────────────────────────────────────────────────────────────────────
      invoices: {
        index: () => organizationInvoicesPath(slug),
        show: (id: RouteId) => organizationInvoicePath(slug, id),
        uploadNew: (options?: { purchase_request_id?: RouteId }) =>
          uploadNewOrganizationInvoicesPath(slug, options),
        comments: (id: RouteId) => organizationInvoiceCommentsPath(slug, id),
        items: () => organizationInvoiceItemsPath(slug),
        markAsReviewed: (id: RouteId) =>
          markAsReviewedOrganizationInvoicePath(slug, id),
        createDirectPurchase: (id: RouteId) =>
          createDirectPurchaseOrganizationInvoicePath(slug, id),
      },

      // ─────────────────────────────────────────────────────────────────────
      // Prices
      // ─────────────────────────────────────────────────────────────────────
      prices: {
        index: () => organizationPricesPath(slug),
      },

      // ─────────────────────────────────────────────────────────────────────
      // Approvals
      // ─────────────────────────────────────────────────────────────────────
      approvals: {
        index: () => organizationApprovalsPath(slug),
        workflows: {
          importNew: () => importNewOrganizationApprovalsWorkflowsPath(slug),
          import: () => importOrganizationApprovalsWorkflowsPath(slug),
          export: () => exportOrganizationApprovalsWorkflowsPath(slug),
        },
      },
    };
  }, [organizationSlug]);
}

/** Tipo de retorno del hook para tipado externo */
export type OrganizationRoutes = ReturnType<typeof useOrganizationRoutes>;
